import { useCallback, useEffect, useState } from "react";
import type { CreditBalance, UserProfile } from "@chuzi/shared/types";
import { api } from "../lib/api";

export function useCreateCreditsGate(user: UserProfile | null = null) {
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [creditsNeeded, setCreditsNeeded] = useState(1);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [balanceRes, estimateRes] = await Promise.all([
        api.credits.balance(),
        api.ai.storyEstimate({ max_scenes: 2, character_count: 2, vibes: [] }),
      ]);
      setBalance(balanceRes.balance);
      setCreditsNeeded(estimateRes.estimate.credits_needed);
    } catch {
      try {
        const balanceRes = await api.credits.balance();
        setBalance(balanceRes.balance);
      } catch {
        setBalance(null);
      }
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    function onCreditsUpdated(e: Event) {
      const detail = (e as CustomEvent<CreditBalance>).detail;
      if (detail) {
        setBalance(detail);
      } else {
        void refresh();
      }
    }
    window.addEventListener("chuzi:credits-updated", onCreditsUpdated);
    return () => window.removeEventListener("chuzi:credits-updated", onCreditsUpdated);
  }, [refresh]);

  const canAfford =
    !!user?.is_admin ||
    (loaded && balance != null && balance.create >= creditsNeeded);

  return { balance, creditsNeeded, canAfford, loaded, refresh };
}
